'use client'

/**
 * LeagueTable — the generic sortable league table for /data pages
 * (FRIDGE-450: "every table sorts", "every entity name is a link").
 *
 * The last four dead tables (dimensions, space-efficiency, the two
 * wine-beverage tables) were hand-rolled server <table> markup: rows already
 * linked their entities but nothing sorted. SortableTable's column defs carry
 * functions, which cannot cross the server→client boundary — so pages pass
 * PLAIN data (column descriptors + cell values + an optional href per row)
 * and this wrapper builds the functions. Same boundary pattern as
 * series-tables and preview-table.
 *
 * Cells are pre-formatted strings paired with a raw sort value, so the page
 * keeps using its own formatters (formatIn, formatSpec, …) server-side and
 * the client ships zero formatting logic.
 */

import Link from 'next/link'
import { SortableTable } from './sortable-table'

export interface LeagueCell {
  /** Pre-formatted display string (the page's own formatter output). */
  text: string
  /** Raw value for sorting; falls back to the text when omitted. */
  sort?: number | string
}

export interface LeagueRow {
  key: string
  /** First-column label; renders as a Link when href is present. */
  label: string
  href?: string | null
  /** Optional shop-chip links rendered after the label (wine-bev bands). */
  chips?: Array<{ href: string; text: string; title?: string }>
  cells: LeagueCell[]
}

export function LeagueTable({
  caption,
  firstHeader,
  headers,
  rows,
  minWidth = 720,
  defaultSortKey,
  defaultDir = 'desc',
}: {
  caption: string
  /** Header of the entity (first) column. */
  firstHeader: string
  /** Headers of the value columns, in cell order. */
  headers: string[]
  rows: LeagueRow[]
  minWidth?: number
  /** Column key to sort by initially — 'label' or `c<index>`. */
  defaultSortKey?: string
  defaultDir?: 'asc' | 'desc'
}) {
  const columns = [
    {
      key: 'label',
      header: firstHeader,
      numeric: false,
      sortValue: (r: LeagueRow) => r.label,
      render: (r: LeagueRow) => (
        <>
          {r.href ? (
            <Link href={r.href} className="font-semibold text-[#0469f0] hover:underline">
              {r.label}
            </Link>
          ) : (
            <span className="font-semibold">{r.label}</span>
          )}
          {r.chips && r.chips.length > 0 && (
            <span className="ml-2 inline-flex flex-wrap gap-1 align-middle">
              {r.chips.map((c) => (
                <Link
                  key={c.href}
                  href={c.href}
                  title={c.title}
                  className="inline-flex items-center rounded-full border border-gray-200 px-2 py-0.5 text-[11px] font-semibold text-[#0469f0] hover:border-[#0469f0] transition-colors"
                >
                  {c.text}
                </Link>
              ))}
            </span>
          )}
        </>
      ),
    },
    ...headers.map((header, j) => ({
      key: `c${j}`,
      header,
      numeric: true,
      sortValue: (r: LeagueRow) => r.cells[j]?.sort ?? r.cells[j]?.text ?? '',
      render: (r: LeagueRow) => r.cells[j]?.text ?? '',
    })),
  ]
  return (
    <SortableTable
      caption={caption}
      columns={columns}
      rows={rows}
      rowKey={(r) => r.key}
      minWidth={minWidth}
      defaultSortKey={defaultSortKey}
      defaultDir={defaultDir}
    />
  )
}
